/**
 * prelim-api.ts
 *
 * API client for prelim page generation endpoints
 */

import { apiRequest } from './api';
import type { CopyrightSearchResult } from './copyright-api';

export interface PrelimOverrides {
  isDissertation: boolean;
  isPLClassics: boolean;
  hasFunding: boolean;
  hasCoverImageCredit: boolean;
  hasExternalDesigner: boolean;
  dissertationStatement: string | null;
  fundingStatement: string | null;
  coverImageCredit: string | null;
  coverDesignerName: string | null;
  depotLegalNumber: string | null;
  imprintOverride: string | null;
  languageOverride: string | null;
}

export interface PrelimPageOutput {
  type: 'half-title' | 'series-page' | 'title-page' | 'copyright-page';
  xml: string;
  label: string;
}

export const DEFAULT_OVERRIDES: PrelimOverrides = {
  isDissertation: false,
  isPLClassics: false,
  hasFunding: false,
  hasCoverImageCredit: false,
  hasExternalDesigner: false,
  dissertationStatement: null,
  fundingStatement: null,
  coverImageCredit: null,
  coverDesignerName: null,
  depotLegalNumber: null,
  imprintOverride: null,
  languageOverride: null,
};

/**
 * Search titles by ISBN, TitleId, or name
 */
export async function searchPrelimTitles(
  query: string,
  limit: number = 20
): Promise<CopyrightSearchResult[]> {
  const params = new URLSearchParams({ q: query, limit: limit.toString() });
  const response = await apiRequest('GET', `/api/prelim/search?${params.toString()}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to search titles');
  }

  const data = await response.json();
  return data.results;
}

/**
 * Get full prelim data for a title
 */
export async function getPrelimData(titleId: number): Promise<any> {
  const response = await apiRequest('GET', `/api/prelim/${titleId}`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch title data');
  }

  return response.json();
}

/**
 * Generate all prelim pages with overrides
 */
export async function generatePrelimPages(
  titleId: number,
  overrides: PrelimOverrides
): Promise<PrelimPageOutput[]> {
  const response = await apiRequest('POST', `/api/prelim/${titleId}/generate`, overrides);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to generate prelim pages');
  }

  const data = await response.json();
  return data.pages;
}

/**
 * Download a single prelim page as XML file
 */
export function downloadPrelimPage(
  page: PrelimPageOutput,
  titleId: number,
  title: string
): void {
  const blob = new Blob([page.xml], { type: 'application/xml' });
  const safeName = title.replace(/[^a-zA-Z0-9-_ ]/g, '').replace(/\s+/g, '-').substring(0, 50);
  const filename = `prelim-${page.type}-${titleId}-${safeName}.xml`;

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

/**
 * Download all prelim pages as individual XML files
 */
export function downloadAllPrelimPages(
  pages: PrelimPageOutput[],
  titleId: number,
  title: string
): void {
  for (const page of pages) {
    downloadPrelimPage(page, titleId, title);
  }
}
